import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Filter, X, ArrowUpDown } from 'lucide-react';

const DynamicFilters = ({ filters, onFiltersChange, onClearFilters, onSortChange }) => {
  const [activeFilters, setActiveFilters] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    onFiltersChange(activeFilters);
  }, [activeFilters, onFiltersChange]);

  useEffect(() => {
    if (onSortChange) {
      onSortChange({ sortBy, sortDirection });
    }
  }, [sortBy, sortDirection, onSortChange]);

  const handleFilterChange = (key, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateRangeChange = (type, value) => {
    const newDateRange = { ...dateRange, [type]: value };
    setDateRange(newDateRange);
    
    if (newDateRange.start && newDateRange.end) {
      handleFilterChange('registered_at', {
        start: newDateRange.start,
        end: newDateRange.end
      });
    }
  };

  const handleMultiSelectChange = (key, value) => {
    const currentValues = activeFilters[key] || [];
    let newValues;
    
    if (currentValues.includes(value)) {
      newValues = currentValues.filter(v => v !== value);
    } else {
      newValues = [...currentValues, value];
    }
    
    handleFilterChange(key, newValues.length > 0 ? newValues : null);
  };

  const clearFilter = (key) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    
    if (key === 'registered_at') {
      setDateRange({ start: '', end: '' });
    }
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setDateRange({ start: '', end: '' });
    onClearFilters();
  };

  const renderFilter = (filter) => {
    switch (filter.type) {
      case 'date':
        return (
          <div key={filter.key} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{filter.label}</label>
            <div className="flex space-x-2">
              <Input
                type="date"
                placeholder="Start date"
                value={dateRange.start}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="flex-1"
              />
              <Input
                type="date"
                placeholder="End date"
                value={dateRange.end}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="flex-1"
              />
            </div>
          </div>
        );

      case 'multi':
        return (
          <div key={filter.key} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{filter.label}</label>
            <Select
              value={activeFilters[filter.key] || ''}
              onValueChange={(value) => handleFilterChange(filter.key, value === 'all' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={filter.placeholder || `Select ${filter.label}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {filter.values
                  ?.filter((value) => {
                    if (value === null || value === undefined) return false;
                    if (typeof value === 'string') return value.trim() !== '';
                    return true;
                  })
                  .map((value) => (
                    <SelectItem key={String(value)} value={String(value)}>
                      {String(value)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'text':
        return (
          <div key={filter.key} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{filter.label}</label>
            <Input
              placeholder={filter.placeholder || `Search ${filter.label.toLowerCase()}`}
              value={activeFilters[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value || null)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearAllFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Sorting Section */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-3">
            <ArrowUpDown className="h-4 w-4" />
            <span className="text-sm font-medium text-gray-700">Sort By</span>
          </div>
          <div className="flex space-x-4">
            <div className="flex-1">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field to sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt">Registration Date</SelectItem>
                  <SelectItem value="userId">User ID</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="source">Source</SelectItem>
                  <SelectItem value="formData.Name">Name</SelectItem>
                  <SelectItem value="formData.Email">Email</SelectItem>
                  <SelectItem value="formData.Company">Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Select value={sortDirection} onValueChange={setSortDirection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filters.map(renderFilter)}
        </div>
        
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters).map(([key, value]) => (
                <div key={key} className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm">
                  <span className="font-medium">{key}:</span>
                  <span>
                    {Array.isArray(value) 
                      ? value.join(', ') 
                      : typeof value === 'object' && value !== null
                        ? value.start && value.end 
                          ? `${value.start} to ${value.end}`
                          : JSON.stringify(value)
                        : value
                    }
                  </span>
                  <button
                    onClick={() => clearFilter(key)}
                    className="ml-1 hover:text-blue-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DynamicFilters;
